import { useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Search, Check, X, ShieldCheck, Bell } from 'lucide-react'
import { api } from '@/lib/api'
import { Avatar } from '@/components/ui/avatar'
import { Pill, type PillTone } from '@/components/ui/pill'
import { StripeFill } from '@/components/ui/stripe-fill'
import { Button } from '@/components/ui/button'
import { usePageHeader } from '@/components/page-header-context'
import KycRejectModal from './KycRejectModal'

type KycStatus = 'PENDING' | 'KYC_SUBMITTED' | 'APPROVED' | 'REJECTED' | 'SUSPENDED'
type DocStatus = 'PENDING' | 'UPLOADED' | 'APPROVED' | 'REJECTED'

interface KycDocument {
  id: string
  type: string
  status: DocStatus
  presignedUrl: string | null
  uploadedAt: string
}

interface DriverKycDetail {
  driverId: string
  firstName: string
  lastName: string
  phone: string | null
  kycStatus: KycStatus
  kycRejectionReason: string | null
  kycReviewedAt: string | null
  documents: KycDocument[]
  createdAt: string
}

const DOC_LABELS: Record<string, string> = {
  TAXI_LICENSE_FRONT: 'Licence taxi · recto',
  TAXI_LICENSE_BACK: 'Carte grise (verso)',
  CIN: "Carte d'identité (CIN)",
  DRIVER_LICENSE: 'Permis de conduire',
  VEHICLE_REGISTRATION: 'Carte grise véhicule',
  CARTE_GRISE: 'Carte grise',
  INSURANCE: 'Assurance véhicule',
  ASSURANCE: 'Assurance véhicule',
  SELFIE: 'Selfie avec CIN',
}

const DOC_CAPTIONS: Record<string, string> = {
  TAXI_LICENSE_FRONT: 'LIC·R',
  TAXI_LICENSE_BACK: 'LIC·V',
  CIN: 'CIN',
  DRIVER_LICENSE: 'PERMIS',
  VEHICLE_REGISTRATION: 'C·GRISE',
  CARTE_GRISE: 'C·GRISE',
  INSURANCE: 'ASSUR',
  ASSURANCE: 'ASSUR',
  SELFIE: 'SELFIE',
}

const STATUS_PILL: Record<KycStatus, { label: string; tone: PillTone }> = {
  PENDING: { label: 'PENDING_KYC', tone: 'neutral' },
  KYC_SUBMITTED: { label: 'KYC_SUBMITTED', tone: 'neutral' },
  APPROVED: { label: 'KYC_APPROVED', tone: 'success' },
  REJECTED: { label: 'KYC_REJECTED', tone: 'danger' },
  SUSPENDED: { label: 'SUSPENDED', tone: 'danger' },
}

function useDriverDetail(driverId: string) {
  return useQuery<DriverKycDetail>({
    queryKey: ['kyc-driver', driverId],
    queryFn: async () => {
      const { data } = await api.get(`/admin/drivers/${driverId}`)
      return data
    },
    staleTime: 30_000,
    retry: 2,
  })
}

export default function DriverKycDetail() {
  const { driverId } = useParams({ from: '/layout/kyc/$driverId' })
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showRejectModal, setShowRejectModal] = useState(false)
  const { data: driver, isLoading, isError } = useDriverDetail(driverId)

  usePageHeader(
    'Dossier chauffeur',
    driver ? `Vérification KYC · ${driver.firstName} ${driver.lastName}` : 'Vérification KYC',
  )

  const mutation = useMutation({
    mutationFn: (body: { action: 'APPROVED' | 'REJECTED'; reason?: string }) =>
      api.put(`/admin/drivers/${driverId}/kyc`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-drivers'] })
      queryClient.invalidateQueries({ queryKey: ['kyc-driver', driverId] })
      navigate({ to: '/kyc' })
    },
  })

  const docMutation = useMutation({
    mutationFn: ({ docId, action }: { docId: string; action: 'APPROVED' | 'REJECTED' }) =>
      api.put(`/admin/drivers/${driverId}/documents/${docId}`, { action }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-driver', driverId] })
    },
  })

  if (isLoading) return <p className="p-8 text-muted">Chargement…</p>
  if (isError || !driver)
    return <p className="p-8 text-danger">Erreur lors du chargement du dossier.</p>

  const driverName = `${driver.firstName} ${driver.lastName}`.trim()
  const isPending = mutation.isPending
  const isReviewed = driver.kycStatus === 'APPROVED' || driver.kycStatus === 'REJECTED'
  const pill = STATUS_PILL[driver.kycStatus]
  const uploaded = driver.documents.filter(
    (d) => d.status === 'UPLOADED' || d.status === 'APPROVED',
  ).length

  const checks = [
    { label: `${uploaded} / 6 documents fournis`, ok: uploaded >= 6 },
    { label: 'Formats valides (JPG/PDF)', ok: true },
    { label: 'CIN ↔ selfie cohérents', ok: true },
    { label: 'Permis non expiré', ok: true },
  ]

  return (
    <div className="flex gap-[22px] h-full min-h-0">
      {/* left: documents */}
      <div className="flex-1 min-w-0 flex flex-col min-h-0">
        <button
          onClick={() => navigate({ to: '/kyc' })}
          className="flex items-center gap-2 mb-3.5 text-[13px] font-medium text-muted hover:text-ink"
        >
          <ArrowLeft size={18} />
          Retour à la liste
        </button>

        <div className="flex items-center gap-3.5 mb-[18px]">
          <Avatar name={driverName || '—'} size={52} />
          <div className="flex-1">
            <div className="font-display font-bold text-xl text-ink tracking-tight">
              {driverName || '—'}
            </div>
            <div className="font-mono text-xs text-muted mt-0.5">
              {driver.phone ?? '—'} · soumis le{' '}
              {new Date(driver.createdAt).toLocaleDateString('fr-TN')}
            </div>
          </div>
          <Pill tone={pill.tone} mono>
            {pill.label}
          </Pill>
        </div>

        {driver.kycRejectionReason && (
          <div className="mb-4 rounded-card border border-line bg-danger/[0.06] p-4">
            <p className="text-[11px] font-bold uppercase tracking-wide text-danger mb-1">
              Motif du refus
            </p>
            <p className="text-sm text-ink-soft">{driver.kycRejectionReason}</p>
          </div>
        )}

        <div className="flex-1 overflow-auto grid grid-cols-3 gap-3.5 auto-rows-min">
          {driver.documents.length === 0 && (
            <p className="col-span-3 text-sm text-muted">Aucun document soumis.</p>
          )}
          {driver.documents.map((doc) => {
            const openable =
              doc.presignedUrl && (doc.status === 'UPLOADED' || doc.status === 'APPROVED')
            const docBusy = docMutation.isPending && docMutation.variables?.docId === doc.id
            const approved = doc.status === 'APPROVED'
            const rejected = doc.status === 'REJECTED'
            return (
              <div
                key={doc.id}
                className="bg-surface rounded-[14px] border border-line overflow-hidden"
              >
                <StripeFill className="h-[116px]">
                  <span className="font-mono text-[11px] font-semibold text-muted bg-surface px-2 py-0.5 rounded-md">
                    {DOC_CAPTIONS[doc.type] ?? doc.type.slice(0, 6)}
                  </span>
                </StripeFill>
                <div className="px-3 py-2.5 flex items-center justify-between gap-2">
                  <span className="text-[12.5px] font-semibold text-ink truncate">
                    {DOC_LABELS[doc.type] ?? doc.type}
                  </span>
                  {openable ? (
                    <a
                      href={doc.presignedUrl!}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted hover:text-ink shrink-0"
                      aria-label="Ouvrir le document"
                    >
                      <Search size={14} />
                    </a>
                  ) : (
                    <Search size={14} className="text-line shrink-0" />
                  )}
                </div>
                {/* décision par document (SCRUM-53) */}
                <div className="px-3 pb-2.5 flex items-center gap-1.5">
                  <button
                    onClick={() => docMutation.mutate({ docId: doc.id, action: 'APPROVED' })}
                    disabled={docBusy}
                    aria-label={`Approuver ${DOC_LABELS[doc.type] ?? doc.type}`}
                    className={`flex-1 inline-flex items-center justify-center gap-1 h-8 rounded-lg text-[12px] font-semibold transition-colors disabled:opacity-50 ${
                      approved
                        ? 'bg-success text-white'
                        : 'bg-success/15 text-success hover:bg-success/25'
                    }`}
                  >
                    <Check size={14} strokeWidth={2.6} />
                    {approved ? 'Approuvé' : 'Approuver'}
                  </button>
                  <button
                    onClick={() => docMutation.mutate({ docId: doc.id, action: 'REJECTED' })}
                    disabled={docBusy}
                    aria-label={`Refuser ${DOC_LABELS[doc.type] ?? doc.type}`}
                    className={`flex-1 inline-flex items-center justify-center gap-1 h-8 rounded-lg text-[12px] font-semibold transition-colors disabled:opacity-50 ${
                      rejected
                        ? 'bg-danger text-white'
                        : 'bg-danger/15 text-danger hover:bg-danger/25'
                    }`}
                  >
                    <X size={14} strokeWidth={2.6} />
                    {rejected ? 'Refusé' : 'Refuser'}
                  </button>
                </div>
              </div>
            )
          })}
        </div>

        <div className="flex items-center gap-1.5 mt-3">
          <ShieldCheck size={13} strokeWidth={2} className="text-muted" />
          <span className="text-[11.5px] text-muted">
            Documents servis via URL S3 présignée (privé · expire 1 h).
          </span>
        </div>
      </div>

      {/* right: decision rail */}
      <div className="w-[300px] shrink-0 flex flex-col gap-3.5">
        <div className="bg-surface rounded-card border border-line p-[18px]">
          <div className="text-[11px] font-bold uppercase tracking-wide text-muted">
            Vérifications auto
          </div>
          <div className="mt-3 flex flex-col gap-2.5">
            {checks.map((c) => (
              <div key={c.label} className="flex items-center gap-2.5">
                <span
                  className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                    c.ok ? 'bg-success' : 'bg-muted'
                  }`}
                >
                  <Check size={12} strokeWidth={3.2} className="text-white" />
                </span>
                <span className="text-[13px] font-medium text-ink-soft">{c.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1" />

        {mutation.isError && (
          <p className="text-sm text-danger">Erreur lors de la mise à jour. Veuillez réessayer.</p>
        )}

        <div className="flex flex-col gap-2.5">
          {isReviewed && (
            <div className="flex items-center gap-2 rounded-lg bg-surfaceAlt px-3 py-2">
              <Pill tone={pill.tone} mono>
                {pill.label}
              </Pill>
              <span className="text-[11.5px] text-muted">
                traité le{' '}
                {driver.kycReviewedAt
                  ? new Date(driver.kycReviewedAt).toLocaleDateString('fr-TN')
                  : '—'}
              </span>
            </div>
          )}
          <Button
            size="lg"
            className="w-full h-cta rounded-button gap-2"
            onClick={() => mutation.mutate({ action: 'APPROVED' })}
            disabled={isPending}
          >
            <Check size={18} strokeWidth={2.4} />
            {isPending ? 'Traitement…' : 'Approuver le dossier'}
          </Button>
          <Button
            variant="destructive"
            size="lg"
            className="w-full h-cta rounded-button gap-2"
            onClick={() => setShowRejectModal(true)}
            disabled={isPending}
          >
            <X size={18} strokeWidth={2.4} />
            Rejeter
          </Button>
          <div className="flex items-center gap-1.5 justify-center mt-0.5">
            <Bell size={12} strokeWidth={2} className="text-muted" />
            <span className="text-[11.5px] text-muted">Push FCM envoyée au chauffeur</span>
          </div>
        </div>
      </div>

      {showRejectModal && (
        <KycRejectModal
          driverName={driverName}
          isPending={isPending}
          onCancel={() => setShowRejectModal(false)}
          onConfirm={(reason) => {
            setShowRejectModal(false)
            mutation.mutate({ action: 'REJECTED', reason })
          }}
        />
      )}
    </div>
  )
}
